import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UploadCloud, Send, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal } from "@/components/Motion";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const MATERIALS = ["SS 304 Stainless Steel", "Powder-Coated MS / GI", "Aluminium", "Not sure — advise me"];
const TIMELINES = ["Immediate", "Within 1 month", "1–3 months", "3–6 months", "Planning stage"];

export default function RequestQuote() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", country: "", city: "",
    product: params.get("product") || "", quantity: "", dimensions: "",
    material: "", application: params.get("application") || "", project_details: "",
    timeline: "", message: params.get("message") || "", website: "",
  });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (fileRef.current?.files?.[0]) fd.append("file", fileRef.current.files[0]);
      const { data } = await api.post("/rfq", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.ok) {
        setSubmitted(true);
        trackEvent("quote_form_submit", { product: form.product });
        window.scrollTo(0, 0);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please check the form and try again.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="rfq-confirmation" className="blueprint-dark flex min-h-[70vh] items-center bg-ink">
        <SEO title="Request Received" path="/request-a-quote" />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
          <CheckCircle2 size={52} className="mx-auto text-emerald-400" />
          <h1 className="mt-8 font-display text-3xl font-medium text-white sm:text-4xl">Thank you.</h1>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            Your requirement has been received. Our team will review the details and contact you shortly.
          </p>
          <a href="/products" data-testid="rfq-confirmation-products-link"
            className="mt-9 inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/50">
            Continue exploring products
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rfq-page">
      <SEO title="Request a Quote" path="/request-a-quote"
        description="Request a quotation from STJ Innovations for cleanroom equipment, pass boxes, LAF units, booths, furniture and precision sheet-metal fabrication." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={[{ label: "Request a Quote" }]} />
          <p className="eyebrow eyebrow-light">RFQ Desk</p>
          <KineticLines lines={["Request a Quote"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            Share your requirement with as much technical detail as you have — specifications, dimensions, materials, application. Our engineering team will review and respond.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <form onSubmit={submit} className="border border-line bg-white p-7 sm:p-10" data-testid="rfq-form">
            <p className="eyebrow">Your Details</p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="rfq-name" className="label-field">Name *</label>
                <input id="rfq-name" data-testid="rfq-name-input" required className="input-field" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="rfq-company" className="label-field">Company</label>
                <input id="rfq-company" data-testid="rfq-company-input" className="input-field" value={form.company} onChange={set("company")} />
              </div>
              <div>
                <label htmlFor="rfq-email" className="label-field">Work Email *</label>
                <input id="rfq-email" data-testid="rfq-email-input" type="email" required className="input-field" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label htmlFor="rfq-phone" className="label-field">Phone</label>
                <input id="rfq-phone" data-testid="rfq-phone-input" type="tel" className="input-field" value={form.phone} onChange={set("phone")} placeholder="+91 / +971 …" />
              </div>
              <div>
                <label htmlFor="rfq-country" className="label-field">Country</label>
                <input id="rfq-country" data-testid="rfq-country-input" className="input-field" value={form.country} onChange={set("country")} />
              </div>
              <div>
                <label htmlFor="rfq-city" className="label-field">City</label>
                <input id="rfq-city" data-testid="rfq-city-input" className="input-field" value={form.city} onChange={set("city")} />
              </div>
            </div>

            <p className="eyebrow mt-12">Your Requirement</p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="rfq-product" className="label-field">Product / Solution</label>
                <select id="rfq-product" data-testid="rfq-product-select" className="input-field" value={form.product} onChange={set("product")}>
                  <option value="">Select a product or solution…</option>
                  <optgroup label="Product Categories">
                    {categories.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="Products">
                    {products.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                  </optgroup>
                  <option value="Custom / Other">Custom / Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="rfq-quantity" className="label-field">Quantity</label>
                <input id="rfq-quantity" data-testid="rfq-quantity-input" className="input-field" value={form.quantity} onChange={set("quantity")} />
              </div>
              <div>
                <label htmlFor="rfq-dimensions" className="label-field">Required Dimensions</label>
                <input id="rfq-dimensions" data-testid="rfq-dimensions-input" className="input-field" value={form.dimensions} onChange={set("dimensions")} placeholder="e.g. 600 × 600 × 600 mm" />
              </div>
              <div>
                <label htmlFor="rfq-material" className="label-field">Material Preference</label>
                <select id="rfq-material" data-testid="rfq-material-select" className="input-field" value={form.material} onChange={set("material")}>
                  <option value="">Select material…</option>
                  {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="rfq-application" className="label-field">Application</label>
                <input id="rfq-application" data-testid="rfq-application-input" className="input-field" value={form.application} onChange={set("application")} placeholder="e.g. sterile manufacturing, QC lab" />
              </div>
              <div>
                <label htmlFor="rfq-timeline" className="label-field">Expected Timeline</label>
                <select id="rfq-timeline" data-testid="rfq-timeline-select" className="input-field" value={form.timeline} onChange={set("timeline")}>
                  <option value="">Select timeline…</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="rfq-details" className="label-field">Project Details</label>
                <textarea id="rfq-details" data-testid="rfq-details-input" rows={4} className="input-field" value={form.project_details} onChange={set("project_details")}
                  placeholder="Cleanroom class, room dimensions, wall system, process requirements…" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-field" htmlFor="rfq-file">Upload Drawing / Specification</label>
                <label data-testid="rfq-file-upload" htmlFor="rfq-file"
                  className="flex cursor-pointer items-center gap-4 border border-dashed border-line bg-mist p-6 transition-colors hover:border-accent/50">
                  <UploadCloud size={22} className="shrink-0 text-accent" />
                  <span className="text-sm text-steel">
                    {fileName || "PDF, JPG, PNG, DWG, DOC or DOCX — up to 10 MB"}
                  </span>
                  <input id="rfq-file" ref={fileRef} type="file" className="sr-only"
                    accept=".pdf,.jpg,.jpeg,.png,.dwg,.doc,.docx"
                    onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
                </label>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="rfq-message" className="label-field">Message</label>
                <textarea id="rfq-message" data-testid="rfq-message-input" rows={3} className="input-field" value={form.message} onChange={set("message")} />
              </div>
              <input type="text" name="website" value={form.website} onChange={set("website")}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="absolute -left-[9999px] h-0 w-0 opacity-0" />
            </div>

            {error && <p className="mt-5 border border-red-200 bg-red-50 p-3.5 text-sm text-red-700" data-testid="rfq-error">{error}</p>}

            <button type="submit" data-testid="rfq-submit-button" disabled={sending}
              className="mt-8 inline-flex items-center gap-2 bg-accent px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60">
              {sending ? "Submitting…" : "Submit Requirement"} <Send size={14} />
            </button>
            <p className="mt-4 text-xs leading-relaxed text-slate-400">
              Your details are used only to respond to this enquiry. Specifications and drawings you share are treated as confidential.
            </p>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
